/**
 * Member auth client — same-origin Better Auth client for the membership
 * system. The admin panel's auth is a separate system (see
 * server/lib/admin-auth.ts) and does not use this.
 */
import { createAuthClient } from "better-auth/react";
import { magicLinkClient } from "better-auth/client/plugins";

export const authClient = createAuthClient({
  basePath: "/api/member-auth",
  plugins: [magicLinkClient()],
});

export const { useSession, signIn, signUp, signOut } = authClient;

/** App-relative redirect guard (open-redirect prevention on ?next=). */
export function safeNext(next: string | null | undefined, fallback = "/dashboard"): string {
  if (!next) return fallback;
  if (!next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return fallback;
  return next;
}

/**
 * Where to go once a new account confirms its email. Password signups lose
 * ?next= (the verification link is the server's, and always lands on
 * /auth/verify), so a page that starts a signup with a destination in mind,
 * like the free kit page, leaves it here. Same browser only, one day, one use;
 * anything else falls back to the dashboard.
 */
const AFTER_VERIFY_KEY = "adv:after-verify";
const AFTER_VERIFY_TTL_MS = 24 * 60 * 60 * 1000;

export function rememberAfterVerify(path: string): void {
  try {
    window.localStorage.setItem(AFTER_VERIFY_KEY, JSON.stringify({ path: safeNext(path), at: Date.now() }));
  } catch {
    /* private window: the dashboard is the fallback */
  }
}

export function takeAfterVerify(fallback = "/dashboard"): string {
  try {
    const raw = window.localStorage.getItem(AFTER_VERIFY_KEY);
    window.localStorage.removeItem(AFTER_VERIFY_KEY);
    if (!raw) return fallback;
    const { path, at } = JSON.parse(raw) as { path?: string; at?: number };
    if (!path || !at || Date.now() - at > AFTER_VERIFY_TTL_MS) return fallback;
    return safeNext(path, fallback);
  } catch {
    return fallback;
  }
}
